// @ts-strict-ignore
import { faChevronDown, faChevronUp } from "@fullscript/pro-regular-svg-icons";
import type { ReactNode } from "react";
import { a } from "react-spring";

import type { AviarySize } from "@aviary/types";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";

import { NoStyleButton } from "../Button/NoStyleButton";

import { useAccordionAnimation } from "./useAccordionAnimation";

import * as styles from "./Accordion.styles";

type AccordionAviarySize = Extract<AviarySize, "small" | "medium" | "large">;
interface Props {
  /**
   * Specifies the core component shown in the collapsed accordion trigger
   *
   * @required
   */
  coreTriggerContent: ReactNode;
  /**
   * Specifies the secondary component shown in the collapsed accordion trigger
   *
   * @default null
   */
  secondaryTriggerContent?: ReactNode;
  /**
   * Specifies the height of the accordion
   *
   * @default medium
   */
  size?: AccordionAviarySize;
  /**
   * Specifies the position of the chevron
   *
   * @default end
   */
  chevronPosition?: "start" | "end";
  /**
   * Specifies whether or not the accordion container has a border
   *
   * @default true
   */
  isBorderless?: boolean;
  /**
   * Specifies whether or not the accordion is expanded by default
   *
   * @default false
   */
  isDefaultExpanded?: boolean;
  /**
   * Specifies whether or not the accordion is disabled
   *
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Specifies whether the content has margin
   *
   * @default false
   */
  isContentMarginless?: boolean;
  /**
   * Adds a background colour to the accordion
   */
  isMuted?: boolean;
  /**
   * Centers chevron and seconndary accordion content; only when chevron position is end
   *
   * @default false
   */
  centerAlignTrigger?: boolean;
  /**
   * Children to be rendered as the expanded content
   *
   * @default undefined
   */
  children?: ReactNode;
  /**
   * Callback when the accordion is opened/closed, which passes up the state
   *
   */
  onChange?: (open: boolean) => void;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/interaction/Accordion
 *
 * @deprecated Use `Accordion` from `@fullscript/aviary-web` instead.
 */
const Accordion = ({
  size = "medium",
  chevronPosition = "end",
  isDefaultExpanded = false,
  isDisabled = false,
  isBorderless: isUnderlined = true,
  isContentMarginless = false,
  centerAlignTrigger = false,
  ...props
}: Props) => {
  const { coreTriggerContent, secondaryTriggerContent, children, onChange, ...rest } = props;

  const { setIsExpanded, isExpanded, isAnimating, ref, height, opacity, y } = useAccordionAnimation(
    !isDisabled && isDefaultExpanded
  );

  const isIconExpanded = isExpanded ? faChevronUp : faChevronDown;

  const frameStyles = [
    styles.frame.base,
    isExpanded && !isAnimating && styles.frame.open,
    !isContentMarginless && styles.frame.margin[chevronPosition],
  ];

  const triggerStyles = [
    styles.wrapper.base,
    isDisabled && styles.wrapper.disabled,
    isUnderlined ? styles.wrapper.enabledBorderBottom : styles.wrapper.enabledBorder,
  ];

  const headerPaddingStyle = [
    styles.padding[size],
    isUnderlined && !isDisabled && styles.noHorizontalPadding,
  ];

  const contentPaddingStyle = [
    styles.padding[size],
    styles.noTopPadding,
    isUnderlined && !isDisabled && styles.noHorizontalPadding,
  ];

  const coreTriggerContentStyles = () => {
    if (chevronPosition === "end") {
      return [
        styles.triggerWrapper.coreContent,
        secondaryTriggerContent && styles.triggerWrapper.bothContent,
      ];
    }
    if (chevronPosition === "start") {
      return [
        styles.triggerWrapper.coreContent,
        styles.triggerWrapper.withChevron,
        secondaryTriggerContent && styles.triggerWrapper.bothContent,
      ];
    }
  };

  const secondaryTriggerContentStyles = chevronPosition === "end" &&
    secondaryTriggerContent && [
      styles.triggerWrapper.secondaryContent,
      styles.triggerWrapper.withChevron,
    ];

  const centerTrigger = centerAlignTrigger && styles.centerTrigger;

  const renderChevronIcon = () => (
    <div css={styles.chevronWrapper}>
      <FontAwesomeIcon icon={isIconExpanded} css={styles.chevron} />
    </div>
  );

  const renderTriggerContent = () => {
    if (chevronPosition === "end") {
      return (
        <div css={styles.triggerWrapper.base}>
          <div css={coreTriggerContentStyles()}>{coreTriggerContent}</div>
          <div css={[secondaryTriggerContentStyles, centerTrigger]}>
            {!!secondaryTriggerContent && secondaryTriggerContent}
            {renderChevronIcon()}
          </div>
        </div>
      );
    }

    if (chevronPosition === "start") {
      return (
        <div css={styles.triggerWrapper.base}>
          <div css={coreTriggerContentStyles()}>
            {renderChevronIcon()}
            {coreTriggerContent}
          </div>
          <div>{secondaryTriggerContent}</div>
        </div>
      );
    }
  };

  const handleClick = () => {
    if (isDisabled) return;
    onChange?.(!isExpanded);
    setIsExpanded(!isExpanded);
  };

  return (
    <div css={triggerStyles} {...rest}>
      <NoStyleButton
        onClick={handleClick}
        disabled={isDisabled}
        css={[styles.triggerButton.base, headerPaddingStyle]}
        aria-expanded={isExpanded}
      >
        {renderTriggerContent()}
      </NoStyleButton>
      <a.div
        style={{
          opacity,
          height: isExpanded && !isAnimating ? "auto" : height,
        }}
        css={frameStyles}
      >
        <a.div ref={ref} style={{ y }} css={contentPaddingStyle}>
          {children}
        </a.div>
      </a.div>
    </div>
  );
};

export { Accordion, type Props };
